import React, { useEffect, useState } from 'react';
import { Row, Col, Button, CardText } from 'reactstrap';
import { Control, Field } from 'react-redux-form';
import { FormGroup } from 'components/common';
import { Trans } from 'react-i18next';
import Select from 'react-select';
import { hasBillingAddressData, validateNumericField, validateVatNumber,  } from './validationUtils';

// Re-export for backward compatibility
export { hasBillingAddressData };

const UpdateBillingContactForm = ({
  billingData,
  countryOptions,
  t,
  colStyle,
  checkboxStyle,
  styleCheck,
  onChangeHandler = () => {},
}) => {
  const [currentCountry, setCurrentCountry] = useState(billingData?.billing_address_data?.country || null);

  useEffect(() => {
    onChangeHandler({
      billing_address: billingData.id,
      billing_address_data: billingData
    })
  }, [billingData]);

  // Validation functions are imported from validationUtils

  const handleFormChange = (field, value) => {
    let validatedValue = value;
    
    // Update current country if country field is changed
    if (field === 'country') {
      setCurrentCountry(value);
    }
    
    // Apply validation based on field type and country
    if (field === 'building_number' || field === 'plot_number') {
      validatedValue = validateNumericField(value, 4, currentCountry);
    } else if (field === 'postal_code') {
      validatedValue = validateNumericField(value, 5, currentCountry);
    } else if (field === 'vat_number') {
      validatedValue = validateVatNumber(value, currentCountry);
    }

    onChangeHandler(prevData => ({
      billing_address_data: {
        ...(prevData?.billing_address_data || {}),
        [field]: validatedValue
      }
    }));
  };

  return (
    <>
      <Row>
        <Col md={12}>
          {/* Checkbox at top */}
          <FormGroup model=".is_default">
            <div className="cta-radio-gr">
              <div className="d-flex my-1">
                <Control.checkbox 
                  {...checkboxStyle}
                  model=".billing_address_data.is_default"
                  defaultChecked={billingData.is_default}
                  onChange={(e) => handleFormChange('is_default', e.target.checked)}
                  disabled={true}
                />
                <span {...styleCheck}><Trans>Save as default contact</Trans></span>
              </div>
            </div>
          </FormGroup>
        </Col>
      </Row>

      {/* Personal Information - Side by side */}
      <Row>
        <Col md={6}>
          <FormGroup label={t("Address Person Name *")} model=".billing_address_data.first_name">
            <Control.text
              updateOn="blur"
              placeholder={t("Address Person Name")}
              className="mb-2"
              model=".billing_address_data.first_name"
              onChange={(e) => handleFormChange('first_name', e.target.value)}
              autoComplete="new-password"
              name="billing_first_name_field"
              defaultValue={billingData.first_name}
            />
          </FormGroup>
        </Col>
        
      </Row>

      {/* Email - Full width */}
      <Row>
        <Col md={6}>
          <FormGroup label={t("Email *")}>
            <Control.text
              model=".billing_address_data.email"
              className="rounded"
              placeholder={t("Email")}
              onChange={(e) => handleFormChange('email', e.target.value)}
              autoComplete="new-password"
              name="billing_email_field"
              defaultValue={billingData.email || ''}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup label={t("Contact Number *")} model=".billing_address_data.contact_number">
            <Control.text
              model=".billing_address_data.contact_number"
              className="rounded"
              placeholder={t("Contact Number")}
              onChange={(e) => handleFormChange('contact_number', e.target.value)}
              autoComplete="new-password"
              name="billing_contact_number_field"
              defaultValue={billingData.contact_number || ''}
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Country - Full width for better visibility */}
      <Row>
        <Col>
          <FormGroup label={t("Country *")} model=".billing_address_data.country" addControlClass={false} className="mb-2">
            <Control.text
              style={{ backgroundColor: '#F4F7FE' }}
              model=".billing_address_data.country"
              component={Select}
              options={countryOptions}
              simpleValue
              clearable={false}
              searchable={true}
              placeholder={t('Select...')}
              onChange={(option) => handleFormChange('country', option)}
              defaultValue={billingData.country || ''}
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Address Line - Full width to accommodate longer addresses */}
      <Row>
        <Col>
          <FormGroup label={t("Address Line *")} model=".billing_address_data.address_first_line">
            <Control.text 
              model=".billing_address_data.address_first_line" 
              className="mb-2" 
              placeholder={t("Address Line")}
              onChange={(e) => handleFormChange('address_first_line', e.target.value)}
              autoComplete="new-password"
              name="billing_address_line_field"
              defaultValue={billingData.address_first_line || ''}
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Building and Plot Numbers - Related specific details */}
      <Row>
        <Col md={6}>
          <FormGroup label={currentCountry === 'SA' ? t("Building Number *") : t("Building Number")} model=".billing_address_data.building_number">
            <Control.text 
              maxLength={currentCountry === 'SA' ? "4" : "20"} 
              model=".billing_address_data.building_number" 
              placeholder={currentCountry === 'SA' ? t("Building Number (4 digits)") : t("Building Number")} 
              className="rounded"
              onChange={(e) => handleFormChange('building_number', e.target.value)}
              autoComplete="new-password"
              name="billing_building_number_field"
              defaultValue={billingData.building_number || ''}
            />
            {currentCountry === 'SA' && (
              <small className="text-muted">{t("Must be exactly 4 digits for Saudi Arabia")}</small>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup label={currentCountry === 'SA' ? t("Plot Number *") : t("Plot Number")} model=".billing_address_data.plot_number">
            <Control.text 
              maxLength={currentCountry === 'SA' ? "4" : "20"} 
              model=".billing_address_data.plot_number" 
              placeholder={currentCountry === 'SA' ? t("Plot Number (4 digits)") : t("Plot Number")} 
              className="rounded"
              onChange={(e) => handleFormChange('plot_number', e.target.value)}
              autoComplete="new-password"
              name="billing_plot_number_field"
              defaultValue={billingData.plot_number || ''}
            />
            {currentCountry === 'SA' && (
              <small className="text-muted">{t("Must be exactly 4 digits for Saudi Arabia")}</small>
            )}
          </FormGroup>
        </Col>
      </Row>

      {/* City and State/Region - Logical geographical grouping */}
      <Row>
        <Col md={6}>
          <FormGroup label={t("City/Town *")} model=".billing_address_data.city">
            <Control.text
              updateOn="blur"
              placeholder={t("City/Town")}
              model=".billing_address_data.city"
              className="mb-2"
              onChange={(e) => handleFormChange('city', e.target.value)}
              autoComplete="new-password"
              name="billing_city_field"
              defaultValue={billingData.city || ''}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup label={t("State/Region *")} model=".billing_address_data.region">
            <Control.text
              updateOn="blur"
              placeholder={t("State/Region")}
              model=".billing_address_data.region"
              className="mb-2"
              onChange={(e) => handleFormChange('region', e.target.value)}
              autoComplete="new-password"
              name="billing_region_field"
              defaultValue={billingData.region || ''}
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Postal Code - Full width for emphasis */}
      <Row>
        <Col>
          <FormGroup label={t("Postal/Zip Code *")} model=".billing_address_data.postal_code">
            <Control.text
              updateOn="blur"
              placeholder={currentCountry === 'SA' ? t("Postal/Zip Code (5 digits)") : t("Postal/Zip Code")}
              model=".billing_address_data.postal_code"
              className="mb-2"
              maxLength={currentCountry === 'SA' ? "5" : "10"}
              onChange={(e) => handleFormChange('postal_code', e.target.value)}
              autoComplete="new-password"
              name="billing_postal_code_field"
              defaultValue={billingData.postal_code || ''}
            />
            {currentCountry === 'SA' && (
              <small className="text-muted">{t("Must be exactly 5 digits for Saudi Arabia")}</small>
            )}
          </FormGroup>
        </Col>
      </Row>

      {/* Tax and Registration Numbers */}
      <Row>
        <Col md={6}>
          <FormGroup label={currentCountry === 'SA' ? t("Tax Number *") : t("Tax Number")} model=".billing_address_data.vat_number">
            <Control.text
              updateOn="blur"
              model=".billing_address_data.vat_number"
              placeholder={currentCountry === 'SA' ? t("Tax Number (15 digits, starts & ends with 3)") : t("Tax Number")}
              className="mb-2"
              maxLength={currentCountry === 'SA' ? "15" : "20"}
              onChange={(e) => handleFormChange('vat_number', e.target.value)}
              autoComplete="new-password"
              name="billing_vat_number_field"
              defaultValue={billingData.vat_number || ''}
            />
            {currentCountry === 'SA' && (
              <small className="text-muted">{t("Must be 15 digits, start with 3, and end with 3 for Saudi Arabia")}</small>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup label={currentCountry === 'SA' ? t("Registration Number *") : t("Registration Number")} model=".billing_address_data.registration_number">
            <Control.text
              updateOn="blur"
              model=".billing_address_data.registration_number"
              placeholder={t("Registration Number")}
              className="mb-2"
              onChange={(e) => handleFormChange('registration_number', e.target.value)}
              autoComplete="new-password"
              name="billing_registration_number_field"
              defaultValue={billingData.registration_number || ''}
            />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
};

export default UpdateBillingContactForm;
